// FILE: /src/Components/QueuePage.js (FULLY UPDATED)
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dropdown, Checkbox, Button, Modal, message, Tooltip, Empty } from 'antd';
import moment from "moment";
import momentTimezone from 'moment-timezone';
import { fetchDrafts, deleteScheduledPost, deleteMultipleScheduledPosts } from "../services/group";
import { postApi } from "../services/publish";
import { renderPlatformIcon, renderProfileImage, renderProfileName } from "./ShowProfiles";
import SocialPostGeneratorModal from "./PostUploader/SocialPostGenerator";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import UpgradeModal from "../Components/BuySubscription/UpgradeModal";
import { getOnboardingForGroup } from "../services/group";
import CreateLinkModal from './Publishing/CreateLinkModal';
import QueuePostSkeleton from './Publishing/QueuePostSkeleton';

const POSTS_PER_PAGE = 15;

const QUEUE_VISIBLE_STATUSES = [
'scheduled',
'draft',
'queued',
'publishing',
'processing',
'failed',
'retrying',
'reconnect_required',
];

const ACTIVE_LOCKED_STATUSES = [
'queued',
'publishing',
'processing',
'retrying',
];

const TimeDisplay = ({ time, brandTimezone }) => {
  if (!time) {
    return <span className="text-gray-400">Unscheduled</span>;
  }

  const userTimezone = momentTimezone.tz.guess();
  const brandTime = moment(time).tz(brandTimezone || userTimezone);
  const userTime = moment(time);

  const formattedBrandTime = brandTime.format('MMM D, h:mm A');
  const formattedUserTime = userTime.format('MMM D, h:mm A');

  if (!brandTimezone || brandTimezone === userTimezone) {
    return <span>{formattedBrandTime}</span>;
  }

  return (
    <Tooltip title={`Your time: ${formattedUserTime}`}>
      <span>{formattedBrandTime}</span>
    </Tooltip>
  );
};

const QueueFilters = ({ filters, onFilterChange, availableProfiles, dynamicPlatformItems }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  const statusItems = useMemo(() => [
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'draft', label: 'Drafts' },
    { key: 'queued', label: 'Queued' },
    { key: 'publishing', label: 'Publishing' },
    { key: 'processing', label: 'Processing' },
    { key: 'failed', label: 'Failed' },
    { key: 'retrying', label: 'Retrying' },
    { key: 'reconnect_required', label: 'Reconnect Required' },
    ], []);

  const profileMenuItems = useMemo(() => {
    return (availableProfiles || []).map(p => {
      const profileId = p.platform === "FACEBOOK" ? p.platform_pageId : p.platform_id;
      return {
        key: profileId,
        label: (
          <Checkbox checked={filters.profiles.includes(profileId)} onChange={() => onFilterChange('profiles', profileId)}>
            <div className="flex items-center gap-2">{renderProfileImage(p)} {renderProfileName(p)}</div>
          </Checkbox>
        )
      };
    });
  }, [availableProfiles, filters.profiles, onFilterChange]);

  const platformMenuItems = useMemo(() => {
    return dynamicPlatformItems.map(p => ({
      key: p.key,
      label: (
        <Checkbox checked={filters.platforms.includes(p.key)} onChange={() => onFilterChange('platforms', p.key)}>
          <span className="flex items-center gap-2">{p.icon} {p.label}</span>
        </Checkbox>
      )
    }));
  }, [dynamicPlatformItems, filters.platforms, onFilterChange]);

  const statusMenuItems = useMemo(() => {
    return statusItems.map(s => ({
      key: s.key,
      label: (
        <Checkbox checked={filters.statuses.includes(s.key)} onChange={() => onFilterChange('statuses', s.key)}>
          {s.label}
        </Checkbox>
      )
    }));
  }, [statusItems, filters.statuses, onFilterChange]);

  return (
    <div className="flex flex-wrap gap-4 mb-4 bg-white p-3 rounded-lg border border-gray-200">
      <Dropdown
        menu={{ items: platformMenuItems }}
        trigger={['click']}
        open={openDropdown === 'platform'}
        onOpenChange={(vis) => setOpenDropdown(vis ? 'platform' : null)}
      >
        <Button>Platforms <span className="text-gray-400 ml-1">{filters.platforms.length || ''}</span></Button>
      </Dropdown>

      <Dropdown
        menu={{ items: profileMenuItems }}
        trigger={['click']}
        open={openDropdown === 'profile'}
        onOpenChange={(vis) => setOpenDropdown(vis ? 'profile' : null)}
      >
        <Button>Profiles <span className="text-gray-400 ml-1">{filters.profiles.length || ''}</span></Button>
      </Dropdown>

      <Dropdown
        menu={{ items: statusMenuItems }}
        trigger={['click']}
        open={openDropdown === 'status'}
        onOpenChange={(vis) => setOpenDropdown(vis ? 'status' : null)}
      >
        <Button>Status <span className="text-gray-400 ml-1">{filters.statuses.length || ''}</span></Button>
      </Dropdown>

      <Button disabled>Author</Button>
    </div>
  );
};

const StatusTag = ({ status, failureReason }) => {
  switch (status) {
    case 'scheduled':
      return <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded-full">SCHEDULED</span>;
    case 'draft':
      return <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">DRAFT</span>;
    case 'queued':
return <span className="text-xs font-semibold bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">QUEUED</span>;
case 'publishing':
return <span className="text-xs font-semibold bg-purple-100 text-purple-800 px-2 py-1 rounded-full">PUBLISHING</span>;
case 'processing':
return <span className="text-xs font-semibold bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full">PROCESSING</span>;
case 'retrying':
return <span className="text-xs font-semibold bg-orange-100 text-orange-800 px-2 py-1 rounded-full">RETRYING</span>;
case 'reconnect_required':
return (
<Tooltip title={failureReason || "This profile needs to be reconnected before the post can be published."}>
<span className="text-xs font-semibold bg-pink-100 text-pink-800 px-2 py-1 rounded-full cursor-help">
<ExclamationCircleOutlined className="mr-1" /> RECONNECT
</span>
</Tooltip>
);
case 'failed':
return (
<Tooltip title={failureReason || "The post failed to publish. Please check the content and try again."}>
<span className="text-xs font-semibold bg-red-100 text-red-800 px-2 py-1 rounded-full cursor-help">
<ExclamationCircleOutlined className="mr-1" /> FAILED
</span>
</Tooltip>
);
default:
return null;
  }
};

const QueuePostRow = ({
  post,
  isSelected,
  onSelectPost,
  onDelete,
  onDuplicate,
  onEdit,
  brandTimezone,
  availableProfiles
}) => {
  const { message, status, scheduledTime, media_url, pinterest_thumbnail_url, failureReason, selectedProfiles } = post;

  const isActiveLocked = ACTIVE_LOCKED_STATUSES.includes(status);

  const actionMenuItems = [
  {
  key: 'edit',
  label: isActiveLocked ? 'Edit unavailable while processing' : 'Edit',
  icon: <EditOutlined />,
  disabled: isActiveLocked,
  onClick: () => onEdit(post),
  },
  {
  key: 'duplicate',
  label: 'Duplicate',
  icon: <CopyOutlined />,
  onClick: () => onDuplicate(post),
  },
  {
  key: 'delete',
  label: isActiveLocked ? 'Delete unavailable while processing' : 'Delete',
  icon: <DeleteOutlined />,
  danger: true,
  disabled: isActiveLocked,
  onClick: () => onDelete(post._id),
  },
  ];

  const firstMedia = media_url?.[0];
  const isVideo = firstMedia?.match(/\.(mp4|mov|webm)$/i);
  const thumbnailUrl = pinterest_thumbnail_url || firstMedia;

  const profilesForPost = useMemo(() => {
    return (selectedProfiles || []).map(profileId =>
      availableProfiles.find(p => (p.platform_pageId === profileId || p.platform_id === profileId))
    ).filter(Boolean);
  }, [selectedProfiles, availableProfiles]);

  return (
    <tr className={`bg-white border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      <td className="p-4 w-12">
        <div className="flex items-center justify-center queue-checkbox">
          <Checkbox checked={isSelected} onChange={() => onSelectPost(post._id)} />
        </div>
      </td>

      <td className="px-6 py-4">
        <div className="flex flex-col space-y-2">
          {profilesForPost.map((p) => (
            <div key={p.platform_id || p.platform_pageId} className="flex items-center gap-2">
              <div className="w-6 h-6">{renderProfileImage(p)}</div>
              {renderPlatformIcon(p.platform)}
              <div className="text-xs font-medium">{renderProfileName(p)}</div>
            </div>
          ))}
        </div>
      </td>

      <td className="px-6 py-4">
        {firstMedia ? (
          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center flex-shrink-0 relative overflow-hidden">
            {isVideo ? (
              <>
                <PlayCircleOutlined className="text-xl text-white absolute z-10" />
                <img src={thumbnailUrl} alt="video thumbnail" className="w-full h-full object-cover rounded" />
              </>
            ) : (
              <img src={firstMedia} alt="media preview" className="w-full h-full object-cover rounded" />
            )}
          </div>
        ) : (
          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
            No Media
          </div>
        )}
      </td>

      <td className="px-6 py-4 max-w-xs">
        <p className="truncate">{message || <span className="text-gray-400">No caption</span>}</p>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <TimeDisplay time={scheduledTime} brandTimezone={brandTimezone} />
      </td>

      <td className="px-6 py-4">
        <StatusTag status={status} failureReason={failureReason} />
      </td>

      <td className="px-6 py-4 text-right">
        <Dropdown menu={{ items: actionMenuItems }} trigger={['click']} placement="bottomRight">
          <Button type="text" shape="circle" icon={<EllipsisOutlined />} />
        </Dropdown>
      </td>
    </tr>
  );
};

const QueuePostCard = ({
  post,
  isSelected,
  onSelectPost,
  onDelete,
  onDuplicate,
  onEdit,
  brandTimezone,
  availableProfiles
}) => {
  const { message, media_url, status, scheduledTime, failureReason, selectedProfiles, pinterest_thumbnail_url } = post;
const isActiveLocked = ACTIVE_LOCKED_STATUSES.includes(status);
  const firstMedia = media_url?.[0];
  const isVideo = firstMedia?.match(/\.(mp4|mov|webm)$/i);
  const thumbnailUrl = pinterest_thumbnail_url || firstMedia;

  const profilesForPost = useMemo(() => {
    return (selectedProfiles || []).map(profileId =>
      availableProfiles.find(p => (p.platform_pageId === profileId || p.platform_id === profileId))
    ).filter(Boolean);
  }, [selectedProfiles, availableProfiles]);

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md border flex flex-col ${
        isSelected ? 'border-blue-600 ring-4 ring-blue-200 bg-blue-50' : 'border-gray-200'
      }`}
    >
      <div className="absolute top-3 left-3 z-50 bg-white border border-gray-300 rounded-md shadow-md p-1 queue-checkbox">
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelectPost(post._id);
          }}
        />
      </div>

      <header className="p-4 border-b flex justify-between items-start">
        <div className="flex flex-col space-y-1.5">
          {profilesForPost.slice(0, 2).map((p) => (
            <div key={p.platform_id || p.platform_pageId} className="flex items-center gap-2">
              <div className="w-6 h-6 flex-shrink-0">{renderProfileImage(p)}</div>
              {renderPlatformIcon(p.platform)}
              <div className="text-xs font-semibold truncate">{renderProfileName(p)}</div>
            </div>
          ))}
          {profilesForPost.length > 2 && (
            <div className="text-xs text-gray-500 pl-1">+ {profilesForPost.length - 2} more</div>
          )}
        </div>
        <StatusTag status={status} failureReason={failureReason} />
      </header>

      <div className="p-4 flex-grow cursor-pointer" onClick={() => onEdit(post)}>
        <p className="text-xs text-gray-500 font-semibold mb-2">
          <TimeDisplay time={scheduledTime} brandTimezone={brandTimezone} />
        </p>

        <p className="text-sm text-gray-700 break-words line-clamp-3 mb-3">
          {message || <span className="text-gray-400">No caption</span>}
        </p>

        {firstMedia && (
          <div className="relative rounded-md w-full h-40 bg-black overflow-hidden">
            {isVideo ? (
              <>
                <img src={thumbnailUrl} alt="Post media" className="rounded-md w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <PlayCircleOutlined className="text-white text-4xl" />
                </div>
              </>
            ) : (
              <img src={firstMedia} alt="Post media" className="rounded-md w-full h-full object-cover" />
            )}
          </div>
        )}
      </div>

      <footer className="p-2 border-t flex justify-end items-center gap-1">
        <Button type="text" size="small" icon={<EditOutlined />} disabled={isActiveLocked} onClick={() => onEdit(post)}>
Edit
</Button>
<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onDuplicate(post)}>
Duplicate
</Button>
<Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={isActiveLocked} onClick={() => onDelete(post._id)}>
Delete
</Button>
      </footer>
    </div>
  );
};

const QueueGridView = ({ posts, selectedPostIds, onSelectPost, ...rest }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {posts.map((post) => (
      <QueuePostCard
        key={post._id}
        post={post}
        isSelected={selectedPostIds.includes(post._id)}
        onSelectPost={onSelectPost}
        selectedPostIds={selectedPostIds}
        {...rest}
      />
    ))}
  </div>
);

const QueueListView = ({ posts, ...rest }) => (
  <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
    <table className="w-full text-sm text-left">
      <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
        <tr>
          <th className="p-4 w-12">
            <div className="flex items-center justify-center queue-checkbox">
              <Checkbox
                checked={posts.length > 0 && rest.selectedPostIds.length === posts.length}
                onChange={rest.onSelectAll}
                indeterminate={rest.selectedPostIds.length > 0 && rest.selectedPostIds.length < posts.length}
              />
            </div>
          </th>
          <th className="px-6 py-3">Profiles</th>
          <th className="px-6 py-3">Media</th>
          <th className="px-6 py-3">Content</th>
          <th className="px-6 py-3">Date (Brand Timezone)</th>
          <th className="px-6 py-3">Status</th>
          <th className="px-6 py-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {posts.map((post) => (
          <QueuePostRow
            key={post._id}
            post={post}
            isSelected={rest.selectedPostIds.includes(post._id)}
            {...rest}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const QueuePage = () => {
  const navigate = useNavigate();
  const { selectedGroup, groups, profile: availableProfiles, profileDetails } = useSelector((state) => state.groups);

  const [allPosts, setAllPosts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ platforms: [], profiles: [], statuses: [], authorId: null });
  const [selectedPostIds, setSelectedPostIds] = useState([]);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });
  const [brandTimezone, setBrandTimezone] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedGroup) return;
    setIsLoading(true);
    try {
      const groupId = groups.find((group) => group.name === selectedGroup)?._id;
      if (groupId) {
        const [postsResponse, onboardingData] = await Promise.all([
          fetchDrafts(groupId),
          getOnboardingForGroup(groupId)
        ]);

        setBrandTimezone(onboardingData?.time_zone || null);

        const queuePosts = (postsResponse || []).filter((post) => {
return QUEUE_VISIBLE_STATUSES.includes(post.status);
});

const sortedPosts = queuePosts.sort((a, b) => {
if (a.scheduledTime && b.scheduledTime) return moment(b.scheduledTime) - moment(a.scheduledTime);
if (!a.scheduledTime && b.scheduledTime) return 1;
if (a.scheduledTime && !b.scheduledTime) return -1;
return 0;
});
setAllPosts(sortedPosts);
        setSelectedPostIds([]);
      }
    } catch (error) {
      message.error("Failed to fetch queue data.");
      console.error("[QueuePage] Error fetching queue data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGroup, groups]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dynamicPlatformItems = useMemo(() => {
    if (!allPosts || allPosts.length === 0) return [];
    const platformsInPosts = new Set(allPosts.flatMap(post => post.platforms));
    return Array.from(platformsInPosts).map(platformKey => ({
      key: platformKey,
      label: platformKey.charAt(0) + platformKey.slice(1).toLowerCase(),
      icon: renderPlatformIcon(platformKey)
    }));
  }, [allPosts]);

  const handleFilterChange = (type, value) => {
    setFilters(prev => {
      const currentValues = prev[type] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [type]: newValues };
    });
    setPage(1);
  };

  const handleSelectPost = (postId) => {
    setSelectedPostIds(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const filteredPosts = useMemo(() => {
    return allPosts.filter(post => {
      const statusMatch = filters.statuses.length === 0 || filters.statuses.includes(post.status);
      const platformMatch = filters.platforms.length === 0 || post.platforms.some(p => filters.platforms.includes(p));
      const profileMatch = filters.profiles.length === 0 || (post.selectedProfiles || []).some(p => filters.profiles.includes(p));
      return statusMatch && platformMatch && profileMatch;
    });
  }, [allPosts, filters]);

  const displayedPosts = useMemo(() => {
    return filteredPosts.slice(0, page * POSTS_PER_PAGE);
  }, [filteredPosts, page]);

  const hasMore = displayedPosts.length < filteredPosts.length;

  const handleSelectAll = () => {
    if (selectedPostIds.length === displayedPosts.length) {
      setSelectedPostIds([]);
    } else {
      setSelectedPostIds(displayedPosts.map(p => p._id));
    }
  };

  const handleDelete = async (postId) => {
  const targetPost = allPosts.find((post) => post._id === postId);
  if (targetPost && ACTIVE_LOCKED_STATUSES.includes(targetPost.status)) {
  message.warning("This post is already being published or processed and cannot be deleted right now.");
  return;
  }

  Modal.confirm({
  title: 'Are you sure you want to delete this post?',
  content: 'This action cannot be undone.',
  okText: 'Delete',
  okType: 'danger',
  onOk: async () => {
  try {
  await deleteScheduledPost(postId);
  message.success("Post deleted successfully.");
  fetchData();
  } catch (error) {
  message.error(error.message || "Failed to delete post.");
  }
  },
  });
  };

  const handleDeleteSelected = () => {
    if (selectedPostIds.length === 0) return;
    Modal.confirm({
      title: `Delete ${selectedPostIds.length} post(s)?`,
      content: 'This action cannot be undone.',
      okText: 'Delete Selected',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteMultipleScheduledPosts(selectedPostIds);
          message.success(`${selectedPostIds.length} post(s) deleted.`);
          fetchData();
        } catch (error) {
          message.error(error.message || "Failed to delete posts.");
        }
      },
    });
  };

  const handleDuplicate = async (post) => {
    const { _id, scheduledTime, media_url, ...restOfPost } = post;
    const mediaItemsForPayload = (media_url || []).map(url => ({
      url: url,
      type: /\.(mp4|mov|webm)$/i.test(url) ? 'video' : 'image'
    }));

    const duplicatedPayload = {
      ...restOfPost,
      media_items: mediaItemsForPayload,
      status: 'draft',
      scheduledTime: null,
    };

    try {
      await postApi(duplicatedPayload);
      message.success("Post duplicated as a new draft.");
      fetchData();
    } catch (error) {
      if (error.response?.data?.errorType === 'LIMIT_REACHED') {
        setUpgradeModal({ visible: true, message: error.response.data.message, featureName: 'Posts' });
      } else {
        message.error(error.message || "Failed to duplicate post.");
      }
    }
  };

  const handleEdit = (post) => {
    navigate("/Dashboard/PublishPost", { state: { PublishData: post, isEditing: true } });
  };

  const handleDraftsGenerated = async (generatedPosts) => {
    const groupId = groups.find(g => g.name === selectedGroup)?._id;
    if (!groupId) {
      message.error("Cannot create draft: Active group not found.");
      return;
    }

    const draftPromises = generatedPosts.map(post => {
      const payload = {
        groupId,
        groupName: selectedGroup,
        media_items: post.imageUrls.map(url => ({ url, type: 'image' })),
        media_type: 'image',
        message: post.caption,
        selectedProfiles: [],
        platforms: [],
        status: 'draft',
      };
      return postApi(payload);
    });

    try {
      await Promise.all(draftPromises);
      message.success(`${generatedPosts.length} new draft(s) created!`);
      fetchData();
    } catch (error) {
      message.error("Failed to save one or more drafts.");
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array(8).fill(0).map((_, i) => <QueuePostSkeleton key={i} />)}
        </div>
      );
    }

    if (allPosts.length > 0 && displayedPosts.length === 0) {
      return (
        <div className="text-center py-20 bg-white rounded-lg border">
          <Empty description={<span className="text-gray-500 font-semibold">No posts match your current filters.</span>}>
            <Button type="primary" onClick={() => setFilters({ platforms: [], profiles: [], statuses: [], authorId: null })}>
              Clear All Filters
            </Button>
          </Empty>
        </div>
      );
    }

    if (displayedPosts.length > 0) {
      return (
        <>
          {viewMode === 'grid' ? (
            <QueueGridView
              posts={displayedPosts}
              onSelectPost={handleSelectPost}
              selectedPostIds={selectedPostIds}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onEdit={handleEdit}
              brandTimezone={brandTimezone}
              availableProfiles={availableProfiles}
              profileDetails={profileDetails}
            />
          ) : (
            <QueueListView
              posts={displayedPosts}
              onSelectPost={handleSelectPost}
              selectedPostIds={selectedPostIds}
              onSelectAll={handleSelectAll}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onEdit={handleEdit}
              brandTimezone={brandTimezone}
              availableProfiles={availableProfiles}
              profileDetails={profileDetails}
            />
          )}

          {hasMore && (
            <div className="text-center mt-6">
              <Button onClick={() => setPage(p => p + 1)}>Load More</Button>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="text-center py-20 bg-white rounded-lg border">
        <i className="fa-solid fa-box-open text-4xl text-gray-300 mb-4"></i>
        <p className="text-gray-500 font-semibold">Your queue is empty.</p>
        <p className="text-gray-400 text-sm mb-6">Create a post or generate one with AI to get started.</p>
        <div className="flex items-center justify-center gap-4">
          <Button type="default" size="large" icon={<PlusOutlined />} onClick={() => setIsAiModalOpen(true)}>
            Generate Drafts
          </Button>
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => navigate('/Dashboard/PublishPost')}>
            Schedule Post
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Queue</h2>
        <div className="flex items-center gap-2">
          <Button icon={<i className="fa-solid fa-share-nodes mr-2"></i>} onClick={() => setIsShareModalOpen(true)}>
            Share for Review
          </Button>
          <Button type="default" icon={<PlusOutlined />} onClick={() => setIsAiModalOpen(true)}>
            Generate Drafts
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/Dashboard/PublishPost')}>
            Schedule Post
          </Button>

          <div className="flex items-center gap-2 border border-gray-300 rounded-md p-1 bg-white ml-4">
            <Button
              type={viewMode === 'grid' ? 'primary' : 'text'}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('grid')}
            />
            <Button
              type={viewMode === 'list' ? 'primary' : 'text'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('list')}
            />
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <QueueFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            availableProfiles={availableProfiles}
            dynamicPlatformItems={dynamicPlatformItems}
          />

          {selectedPostIds.length > 0 && (
            <div className="flex items-center gap-2">
              {viewMode === 'grid' && (
                <Button onClick={handleSelectAll}>
                  {selectedPostIds.length === displayedPosts.length ? 'Deselect All' : 'Select All Visible'}
                </Button>
              )}
              <Button type="primary" danger onClick={handleDeleteSelected}>
                Delete Selected ({selectedPostIds.length})
              </Button>
            </div>
          )}
        </div>
      </div>

      {renderContent()}

      <SocialPostGeneratorModal
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        onPostGenerated={handleDraftsGenerated}
        isDraftMode={true}
      />

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />

      <CreateLinkModal
      isOpen={isShareModalOpen}
      onClose={() => setIsShareModalOpen(false)}
      posts={allPosts.filter(p => ['scheduled', 'draft'].includes(p.status))}
      />
    </div>
  );
};

export default QueuePage;